import { z } from "zod";

import { DbEnum } from "../../../generated/prisma-enums";
import {
  VibeAuditCardGrantStatusReadSchema,
  VibeAuditCardProjectionReadSchema,
  VibeAuditEnvVarScopeReadSchema,
  VibeAuditGrantTransitionReasonReadSchema
} from "./read-vocabularies.schema";

/**
 * The coordinate every access-card lifecycle event carries.
 *
 * Three ids and nothing else. The audit row's own `organizationId` and
 * `vibeAppId` columns already name the tenant and the app, so what is left is
 * WHICH authority moved: the grant that holds it, the card it came from, and
 * the credential the card delegates.
 *
 * `credentialId` is recorded even though it is derivable from the card today.
 * `AccessCard` can be deleted outright, and then the card id resolves to
 * nothing while this still answers "whose authority was this?" — which is the
 * question an incident asks first. It carries no secret: it is an id, and the
 * organization reading the feed already owns the credential it names.
 *
 * No card NAME and no policy document. A card's name is mutable and its
 * policies are the thing the pointer model exists to keep out of every surface
 * that is not the broker's decision path; `VibeAppCardGrant.cardPolicySnapshot`
 * is the snapshot that makes an old row resolvable, and it stays there.
 */
const VibeAuditCardGrantShape = {
  /// The grant whose lifecycle moved — the join key back to VibeAppCardGrant.
  vibeAppCardGrantId: z.string().uuid(),
  /// The access card the authority came from.
  accessCardId: z.string().uuid(),
  /// The credential the card delegates.
  credentialId: z.string().uuid()
};

/**
 * The added fields shared by the three grant STATUS events (ISSUED excepted:
 * an issue has no transition reason).
 *
 * `priorStatus` is nullable for one case only — `CARD_GRANT_ISSUED`, where a
 * null means no grant existed at all. Every transition event writes a real
 * status, because a transition by definition came from one.
 */
const VibeAuditCardGrantTransitionShape = {
  ...VibeAuditCardGrantShape,
  /// The status the grant held when the write landed, read inside the same
  /// transaction as the write itself. Not derivable afterwards: the grant row
  /// carries only the CURRENT status, so by the time anyone reads this event
  /// the value that makes it a transition is gone. It is also what separates
  /// the two readings of CARD_GRANT_ACTIVATED — from PAUSED it is a resume,
  /// from PENDING_APPROVAL it is an approver releasing the grant.
  priorStatus: z.enum(DbEnum.VIBE_APP_CARD_GRANT_STATUS_VALUES),
  /// WHY the grant moved. The same enum the grant row's `lastTransitionReason`
  /// column carries, copied onto the audit row rather than joined at read time:
  /// the column holds only the LAST transition, so the next one overwrites the
  /// reason this event is about.
  reason: z.enum(DbEnum.GRANT_TRANSITION_REASON_VALUES)
};

/**
 * The environment coordinate a binding occupies. The PAIR is the coordinate —
 * `VibeAppEnvKey` is keyed on (app, scope, name) — so an event that named only
 * the name would be describing half of what moved.
 */
const VibeAuditCardBindingCoordinateShape = {
  /// The binding row. Still recorded on a REMOVED event, where the row is gone
  /// by the time anyone reads this: it is what ties the removal to the
  /// RENAMED and ISSUED rows that came before it.
  vibeAppCardBindingId: z.string().uuid(),
  name: z.string().min(1),
  scope: z.enum(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES)
};

/**
 * Emitted when an access card is imported into an app's environment — the
 * moment a person delegates credential authority to a machine.
 *
 * Written INSIDE the import's existing composite `$transaction`, as a sixth
 * write alongside the env-name reservation, the broker key, the grant, the
 * ReBAC edge and the binding. The transaction boundary was already the port;
 * this row simply joins it, so a delegation cannot exist without the record of
 * who made it.
 *
 * `actorUserId` on the row IS set and is never a system actor — the import
 * routes carry neither `@PublicAPI` nor `@AllowOrgApiKey`, so every caller
 * presents a JWT. With `DEPLOYMENT_ROLLED_BACK_USER` and
 * `DEPLOYMENT_VERIFICATION_OVERRIDDEN`, one of the few events here that always
 * has a person behind it.
 */
export const VibeAuditCardGrantIssuedPayloadSchema = z.object({
  eventType: z.literal("CARD_GRANT_ISSUED"),
  ...VibeAuditCardGrantShape,
  ...VibeAuditCardBindingCoordinateShape,
  /// NULL when no grant existed for this (app, card) — a first delegation.
  /// Non-null when one did: the import REVIVES the existing grant rather than
  /// creating a second authority (`@@unique([vibeAppId, accessCardId])`), so a
  /// re-import of a REVOKED card reads `REVOKED` here. Recording it is what
  /// stops the feed showing a retired delegation coming back as if it were new.
  priorStatus: z.enum(DbEnum.VIBE_APP_CARD_GRANT_STATUS_VALUES).nullable(),
  /// The status the grant was written at. ACTIVE on every path today;
  /// PENDING_APPROVAL the day separation of duty is switched on.
  grantStatus: z.enum(DbEnum.VIBE_APP_CARD_GRANT_STATUS_VALUES),
  /// How the authority reaches the app. HANDLE is the only projection that
  /// ships; recorded rather than assumed, so a row written today still says
  /// which delivery path it authorised once others exist.
  projection: z.enum(DbEnum.VIBE_CARD_PROJECTION_VALUES),
  /// The revocation guarantee the importer declared, in seconds. 0 means "when
  /// I revoke this, it stops now". It is the one number on this row a human
  /// authored, and the refusal that enforces it reads the same value.
  maxRevocationWindowSeconds: z.number().int().nonnegative().finite()
});
export type VibeAuditCardGrantIssuedPayload = z.infer<typeof VibeAuditCardGrantIssuedPayloadSchema>;

/**
 * Emitted when a grant is reversibly stopped — an operator paused it, or a
 * quota or budget ceiling was reached. Written inside the same transaction as
 * the status flip.
 *
 * Distinct from `CARD_GRANT_REVOKED` because PAUSED is expected to resume.
 * Collapsing the two would make "is this delegation coming back?" answerable
 * only by reading a status column that the next transition overwrites.
 */
export const VibeAuditCardGrantPausedPayloadSchema = z.object({
  eventType: z.literal("CARD_GRANT_PAUSED"),
  ...VibeAuditCardGrantTransitionShape
});
export type VibeAuditCardGrantPausedPayload = z.infer<typeof VibeAuditCardGrantPausedPayloadSchema>;

/**
 * Emitted when a grant becomes ACTIVE — resumed from PAUSED, or released from
 * PENDING_APPROVAL by an approver. Written inside the same transaction as the
 * status flip.
 *
 * One event type per TARGET status, not per (prior, target) pair, so a count of
 * "delegations switched back on" is a GROUP BY over the event type.
 * `priorStatus` carries which of the two readings applies.
 */
export const VibeAuditCardGrantActivatedPayloadSchema = z.object({
  eventType: z.literal("CARD_GRANT_ACTIVATED"),
  ...VibeAuditCardGrantTransitionShape
});
export type VibeAuditCardGrantActivatedPayload = z.infer<
  typeof VibeAuditCardGrantActivatedPayloadSchema
>;

/**
 * Emitted when a grant is terminally stopped. Written inside the same
 * transaction as the status flip, from BOTH revocation paths — the explicit
 * transition, and the implicit revoke when an app removes its LAST binding on a
 * card.
 *
 * The second path is the common one and the easy one to miss: a person clicking
 * "remove from my environment" believes they ended the delegation, and the
 * delete transaction agrees with them. An audit family that only covered the
 * explicit transition would have recorded the rare revocation and missed the
 * routine one.
 *
 * Carries no `revokedAt`: the grant row stamps it in the same transaction, and
 * this row's own `createdAt` is the same instant. A second copy of one
 * timestamp is a second thing that can disagree.
 */
export const VibeAuditCardGrantRevokedPayloadSchema = z.object({
  eventType: z.literal("CARD_GRANT_REVOKED"),
  ...VibeAuditCardGrantTransitionShape
});
export type VibeAuditCardGrantRevokedPayload = z.infer<
  typeof VibeAuditCardGrantRevokedPayloadSchema
>;

/**
 * Emitted when a binding moves to a different environment coordinate — renamed,
 * re-scoped, or both. Written inside the same transaction as the row update and
 * the namespace reserve/release pair.
 *
 * Written only when the coordinate actually MOVED; a PATCH restating the
 * current name changes nothing and writes nothing, the same reason
 * `APP_EDGE_UNROUTED` writes on transition only.
 *
 * The authority is untouched by a rename, so this event does not claim
 * otherwise: no `priorStatus`, no `reason`. What it answers is "what was this
 * variable called before?", which the binding row cannot answer about itself.
 */
export const VibeAuditCardBindingRenamedPayloadSchema = z.object({
  eventType: z.literal("CARD_BINDING_RENAMED"),
  ...VibeAuditCardGrantShape,
  ...VibeAuditCardBindingCoordinateShape,
  /// The coordinate the binding left. Both halves, because the namespace is
  /// keyed on the pair and a scope move with an unchanged name is still a move.
  priorName: z.string().min(1),
  priorScope: z.enum(DbEnum.VIBE_ENV_VAR_SCOPE_VALUES)
});
export type VibeAuditCardBindingRenamedPayload = z.infer<
  typeof VibeAuditCardBindingRenamedPayloadSchema
>;

/**
 * Emitted when a binding is removed from an app's environment. Written inside
 * the same transaction as the delete and the namespace release.
 *
 * ALWAYS written, and PAIRED with `CARD_GRANT_REVOKED` when this was the app's
 * last binding on the card — never replaced by it. Removing one of two bindings
 * ends no authority; without this row it would leave no trace at all.
 *
 * No `grantRevoked` flag: whether the authority ended is told by the presence
 * of the sibling `CARD_GRANT_REVOKED` row from the same transaction, and a
 * boolean restating it would be a second thing that can disagree — the same
 * reasoning that keeps `newStatus` off the cost-safety family.
 */
export const VibeAuditCardBindingRemovedPayloadSchema = z.object({
  eventType: z.literal("CARD_BINDING_REMOVED"),
  ...VibeAuditCardGrantShape,
  ...VibeAuditCardBindingCoordinateShape
});
export type VibeAuditCardBindingRemovedPayload = z.infer<
  typeof VibeAuditCardBindingRemovedPayloadSchema
>;

/**
 * The READ side of this family's branches: what the audit feed's readers parse.
 * Each one widens only its enum fields, to the lenient read vocabularies in
 * `read-vocabularies.schema.ts` — a value a newer backend writes reaches an
 * older reader as that field's unlisted value instead of failing the feed page.
 * The emitting branches above stay strict.
 */
const VibeAuditCardGrantTransitionRead = {
  priorStatus: VibeAuditCardGrantStatusReadSchema,
  reason: VibeAuditGrantTransitionReasonReadSchema
};
const VibeAuditCardBindingScopeRead = { scope: VibeAuditEnvVarScopeReadSchema };

export const VibeAuditCardGrantIssuedReadPayloadSchema =
  VibeAuditCardGrantIssuedPayloadSchema.extend({
    ...VibeAuditCardBindingScopeRead,
    priorStatus: VibeAuditCardGrantStatusReadSchema.nullable(),
    grantStatus: VibeAuditCardGrantStatusReadSchema,
    projection: VibeAuditCardProjectionReadSchema
  });
export const VibeAuditCardGrantPausedReadPayloadSchema =
  VibeAuditCardGrantPausedPayloadSchema.extend(VibeAuditCardGrantTransitionRead);
export const VibeAuditCardGrantActivatedReadPayloadSchema =
  VibeAuditCardGrantActivatedPayloadSchema.extend(VibeAuditCardGrantTransitionRead);
export const VibeAuditCardGrantRevokedReadPayloadSchema =
  VibeAuditCardGrantRevokedPayloadSchema.extend(VibeAuditCardGrantTransitionRead);
export const VibeAuditCardBindingRenamedReadPayloadSchema =
  VibeAuditCardBindingRenamedPayloadSchema.extend({
    ...VibeAuditCardBindingScopeRead,
    priorScope: VibeAuditEnvVarScopeReadSchema
  });
export const VibeAuditCardBindingRemovedReadPayloadSchema =
  VibeAuditCardBindingRemovedPayloadSchema.extend(VibeAuditCardBindingScopeRead);
